/*
 * newsletter-signup.js
 * ------------------------------------------------------------------
 * Welcome success state for the newsletter forms (popup + footer).
 *
 * IMPORTANT - this file does NOT touch form submission.
 * The newsletter form is protected by invisible hCaptcha, which injects its
 * token into the form and then calls the native submit. Intercepting submit
 * (fetch/AJAX) would send the request without the token. So the form posts
 * natively, Shopify redirects back with ?customer_posted=true, and this script
 * detects that on page load and renders the success state.
 *
 * Markup comes from snippets/newsletter-form.liquid (render ... enhance: true),
 * which only outputs <newsletter-signup> when settings.welcome_enabled is on.
 * Loaded (deferred) from sections/newsletter-popup.liquid and sections/footer.liquid;
 * the guard below makes loading it twice harmless.
 *
 * Public helpers are exposed on window.eivogeNewsletter for newsletter-popup.js.
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  // Guard against double init (script is loaded from both the popup and the footer)
  if (window.__eivogeNewsletterInit) return;
  window.__eivogeNewsletterInit = true;

  var FLAG_KEY = 'eivoge_newsletter_subscribed';
  var DEFAULT_SUCCESS = 'Check your inbox for your welcome code.';
  var DEFAULT_THANKS = 'Thanks for subscribing.';

  // ─── Storage helpers ────────────────────────────────────────────
  // Every storage access is wrapped: Safari private mode, blocked cookies and the
  // theme-editor iframe can all throw. A storage failure must never break the UI.
  // (Intentionally not theme.config.hasLocalStorage - it is false inside the editor iframe.)

  function readFlag() {
    try {
      if (localStorage.getItem(FLAG_KEY)) return true;
    } catch (e) {}
    try {
      return document.cookie.split(';').some(function (c) {
        return c.trim().indexOf(FLAG_KEY + '=') === 0;
      });
    } catch (e) {}
    return false;
  }

  function setFlag() {
    try { localStorage.setItem(FLAG_KEY, '1'); } catch (e) {}
    try {
      // Cookie fallback, 10 years (browsers may cap this), site-wide
      document.cookie = FLAG_KEY + '=1;path=/;max-age=315360000;SameSite=Lax';
    } catch (e) {}
  }

  // ─── Live region helper ─────────────────────────────────────────
  // The region is persistent and empty until needed (rendered by the snippet,
  // one per form instance). Clear first, then set after a beat, so screen
  // readers register the change.
  function announce(wrapperEl, message) {
    var region = wrapperEl.querySelector('[role="status"]');
    if (!region) return;
    region.textContent = '';
    setTimeout(function () {
      region.textContent = message;
    }, 100);
  }

  // ─── Success state renderer ─────────────────────────────────────
  // options.returning: use the "returning visitor" text instead of the fresh-signup text
  function showSuccess(wrapperEl, options) {
    if (!wrapperEl) return;
    var returning = !!(options && options.returning);

    var text = returning
      ? (wrapperEl.dataset.thanksText || DEFAULT_THANKS)
      : (wrapperEl.dataset.successText || DEFAULT_SUCCESS);

    // CSS reacts to this attribute (hides heading/text/field, shows the success block)
    wrapperEl.setAttribute('data-state', 'success');

    // Success block - deliberately NOT an .alert (newsletter-popup.js reopens on any .alert)
    var successEl = wrapperEl.querySelector('.newsletter-success');
    var heading = wrapperEl.querySelector('.newsletter-success-heading');
    if (heading) heading.textContent = text;
    if (successEl) successEl.removeAttribute('hidden');

    // Footer: field stays visible but is cleared
    var input = wrapperEl.querySelector('input[type="email"]');
    if (input) input.value = '';

    // HoverButton sets aria-busy on submit and only clears it on pageshow
    var btn = wrapperEl.querySelector('[type="submit"]');
    if (btn) btn.removeAttribute('aria-busy');

    var modal = wrapperEl.closest('newsletter-modal');

    if (!modal) {
      // Footer: announce through the live region, focus stays where the user left it
      announce(wrapperEl, text);
      return;
    }

    // Popup: mirror the state on the modal (focusElement / CSS read it there) and
    // point the dialog's accessible name at the success message
    modal.setAttribute('data-state', 'success');
    if (heading && heading.id) modal.setAttribute('aria-labelledby', heading.id);

    if (modal.hasAttribute('open')) {
      // Modal is already open: move focus now and re-trap (the trap's first/last are stale after the swap)
      setTimeout(function () {
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus();
          if (window.theme && theme.a11y && typeof theme.a11y.trapFocus === 'function') {
            theme.a11y.trapFocus(modal, heading);
          }
        }
        announce(wrapperEl, text);
      }, 50);
    } else {
      // Modal is closed (it reopens ~1s after load): a hidden region is not announced and a hidden
      // element cannot take focus. NewsletterModal.afterShow focuses the success heading via
      // focusElement and re-traps, so only the announcement is deferred until it opens.
      modal.addEventListener('modal:afterShow', function () {
        announce(wrapperEl, text);
      }, { once: true });
    }
  }

  // ─── Page load detection ────────────────────────────────────────
  function onPageLoad() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') !== 'true') return;

    // Only act when enhanced forms exist (they render only when welcome_enabled is on)
    var wrappers = document.querySelectorAll('newsletter-signup[data-enabled="true"]');
    if (!wrappers.length) return;

    setFlag();

    // Clean the URL: drop customer_posted and the hash fragment, keep any other query params
    params.delete('customer_posted');
    var query = params.toString();
    try {
      history.replaceState(null, '', window.location.pathname + (query ? '?' + query : ''));
    } catch (e) {}

    wrappers.forEach(function (wrapper) {
      showSuccess(wrapper, { returning: false });
    });
  }

  // ─── Custom element ─────────────────────────────────────────────
  // Minimal wrapper. All behaviour is page-load driven, not submit driven.
  if (!customElements.get('newsletter-signup')) {
    customElements.define(
      'newsletter-signup',
      class NewsletterSignup extends HTMLElement {
        connectedCallback() {
          if (readFlag()) this.setAttribute('data-subscribed', 'true');
        }
      }
    );
  }

  // Shared with newsletter-popup.js (which has its own readFlag because it can run first)
  window.eivogeNewsletter = {
    readFlag: readFlag,
    setFlag: setFlag,
    showSuccess: showSuccess
  };

  // Script is deferred, so the DOM is normally ready; handle both cases
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onPageLoad);
  } else {
    onPageLoad();
  }
})();
